import { Box, Button, Card, CardContent, CardHeader, Chip, Divider, Grid, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import {
  Save as SaveIcon, ArrowDropDown as ArrowDropDownIcon, Info as InfoIcon, Print as PrintIcon, Cancel as CancelIcon,
  ArrowDropUp as ArrowDropUpIcon, AccountBalance as AccountBalanceIcon, QrCode as QrCodeIcon
} from '@mui/icons-material'
import React, { useEffect, useState } from "react";
import WSTextField, { CurrencyType, TextType } from "../../Components/TextBox/WSTextBox";
import { useForm } from "react-hook-form";
import WSDateField from "../../Components/DateField/WSDateField";
import WSSCheckBoxField from "../../Components/CheckBox/WSCheckBox";
import WSButton from "../../Components/Button/WSButton";
import { useNavigate, useParams } from "react-router-dom";
import { callAPI, dateFormat, Method, updateMainHeader } from "../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";

export const BankAccount: React.FC<any> = React.memo((props) => {
  const { control, register, handleSubmit, setValue } = useForm();

  const [loading, setLoading] = useState(false);
  const [showMoreFields, setShowMoreFields] = useState(false);

  const params = useParams();
  const id = params?.id;

  const { showMessage } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      const fetchData = async () => {
        try {
          const response = await callAPI(
            `Vyapar/BankAccount/${id}`,
            Method.GET
          );

          console.log("fetchData response===>", response);

          if (response && response.data) {
            const bankData = response.data;

            setValue("accountDisplayName", bankData.accountDisplayName || "");
            setValue("accountNumber", bankData.accountNumber || "");
            setValue("ifscCode", bankData.ifscCode || "");
            setValue("upiId", bankData.upiId || "");
            setValue("bankName", bankData.bankName || "");
            setValue("accountHolderName", bankData.accountHolderName || "");
            setValue("openingBalance", bankData.openingBalance || 0);

            // Important: convert ISO date to input date format (yyyy-mm-dd)
            setValue(
              "asOfDate",
              bankData.asOfDate
                ? dateFormat(bankData.asOfDate, 'DD-MM-YYYY HH:mm:ss')
                : ""
            );

            setValue(
              "printUPIQrOnInvoice",
              bankData.printUPIQrOnInvoice ?? false
            );

            setValue(
              "printBankDetailsOnInvoice",
              bankData.printBankDetailsOnInvoice ?? false
            );
          }
        } catch (error) {
          console.error("Failed to fetch bank account data:", error);
        }
      };

      fetchData();
    }
  }, [id, setValue]);

  const handleData = async (data: any) => {
    try {
      setLoading(true);

      const request = {
        accountDisplayName: data.accountDisplayName || "",
        accountNumber: data.accountNumber || "",
        ifscCode: data.ifscCode || "",
        upiId: data.upiId || "",
        bankName: data.bankName || "",
        accountHolderName: data.accountHolderName || "",
        openingBalance: Number(data.openingBalance) || 0,
        asOfDate: data.asOfDate
          ? new Date(data.asOfDate).toISOString()
          : new Date().toISOString(),
        printUPIQrOnInvoice: data.printUPIQrOnInvoice ?? false,
        printBankDetailsOnInvoice: data.printBankDetailsOnInvoice ?? false,
      };

      let response;

      if (id) {
        response = await callAPI(
          `Vyapar/BankAccount/${id}`,
          Method.PUT,
          request
        );
      } else {
        response = await callAPI(
          "Vyapar/BankAccount",
          Method.POST,
          request
        );
      }

      console.log("handleData response===>", response);

      if (response) {
        showMessage(
          response?.message,
          response.success === true ? "success" : "error"
        );

        if (response.success === true) {
          navigate("/Bank-account/list"); // corrected route
        }
      }
    } catch (error) {
      console.error("Failed to save bank account:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    navigate("/");
  };

  useEffect(() => {
    updateMainHeader("Bank Account");
  });

  breadCrumbsStore.updateBreadCrumbs([
    { "Bank Account List": "/Bank-account/list" },
    {
      [!id ? "Add Bank Account" : "Update Bank Account"]:
        "/Bank-account/create",
    },
  ]);

  return (
    <Box >
      <Grid container spacing={3}>
        {/* Main Form Card */}
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              marginTop: 2,
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Bank Account Information
                  </Typography>
                  <Tooltip title="Fill in the basic account details">
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              subheader="Enter the primary details of your bank account"
            />

            <CardContent sx={{ p: 4 }}>
              <Grid container spacing={3}>
                {/* Account Display Name */}
                <Grid item xs={12} md={6}>
                  <WSTextField
                    label="Account Display Name"
                    {...register("accountDisplayName")}
                    id="accountDisplayName"
                    name="accountDisplayName"
                    control={control}
                    rules={{ required: "Display name is required" }}
                  />
                </Grid>

                {/* Opening Balance & As Of Date - Side by side */}
                <Grid item xs={12} md={3}>
                  <WSTextField
                    label="Opening Balance"
                    {...register("openingBalance")}
                    id="openingBalance"
                    name="openingBalance"
                    control={control}
                    rules={{ required: false }}
                    type={TextType.Currency}
                    currency={CurrencyType.Rupee}

                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <WSDateField
                    id="asOfDate"
                    name="asOfDate"
                    label="As Of Date"
                    dateFormat="DD-MM-YYYY"
                    denyFuturOrPast="custom"
                    control={control}
                    rules={{ required: false }}
                  />
                </Grid>

                {/* Payment Details Section Toggle */}
                <Grid item xs={12}>
                  <Box sx={{ mt: 1, mb: showMoreFields ? 2 : 0 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowMoreFields(!showMoreFields)}
                      endIcon={showMoreFields ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',

                      }}
                    >
                      {showMoreFields ? 'Hide Payment Details' : 'Add Payment Details'}
                    </Button>
                  </Box>
                </Grid>

                {/* Additional Payment Fields */}
                {showMoreFields && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}>
                        <Chip
                          label="Payment Information"
                          size="small"
                        />
                      </Divider>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <WSTextField
                        label="Account Number"
                        id="accountNumber"
                        name="accountNumber"
                        control={control}
                        type={TextType.AllowOnlyNumber}
                        rules={{
                          required: "Account number is required",
                          minLength: {
                            value: 9,
                            message: "Account number must be at least 9 digits",
                          },
                          maxLength: {
                            value: 18,
                            message: "Account number cannot exceed 18 digits",
                          },
                        }}

                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <WSTextField
                        label="IFSC Code"
                        {...register("ifscCode")}
                        id="ifscCode"
                        name="ifscCode"
                        control={control}
                        rules={{ required: "IFSC code is required" }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <WSTextField
                        label="UPI ID for QR Code"
                        {...register("upiId")}
                        id="upiId"
                        name="upiId"
                        control={control}
                        suffix={<QrCodeIcon />}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <WSTextField
                        label="Bank Name"
                        {...register("bankName")}
                        id="bankName"
                        name="bankName"
                        control={control}
                        rules={{ required: "Bank name is required" }}

                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <WSTextField
                        label="Account Holder Name"
                        {...register("accountHolderName")}
                        id="accountHolderName"
                        name="accountHolderName"
                        control={control}
                        rules={{ required: "Account holder name is required" }}

                      />
                    </Grid>
                  </>
                )}

                {/* Invoice Preferences Section */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Chip
                      label="Invoice Preferences"
                      size="small"

                    />
                  </Divider>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                    }}
                  >
                    <WSSCheckBoxField
                      label={"Print UPI QR Code on Invoices"}
                      id="printUPIQrOnInvoice"
                      control={control}
                      {...register("printUPIQrOnInvoice")}
                      name="printUPIQrOnInvoice"
                      rules={{ required: false }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 5 }}>
                      Customers can scan and pay via UPI apps
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,

                    }}
                  >
                    <WSSCheckBoxField
                      label={"Print Bank Details on Invoices"}
                      id="printBankDetailsOnInvoice"
                      control={control}
                      {...register("printBankDetailsOnInvoice")}
                      name="printBankDetailsOnInvoice"
                      rules={{ required: false }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 5 }}>
                      Include bank transfer details on invoices
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Form Actions */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,

              backdropFilter: 'blur(8px)',

              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              position: 'sticky',
              bottom: 20,
              zIndex: 10
            }}
          >

            <WSButton
              id="cancelBtn"
              variant="outlined"
              color="error"
              size="small"
              label="Cancel"
              onClick={handleCancelClick}
              startIcon={<CancelIcon />}
            />

            <WSButton
              id="saveBtn"
              variant="contained"
              color="primary"
              size="small"
              type="submit"
              onClick={handleSubmit(handleData)}
              startIcon={<SaveIcon />}
              disabled={loading}
              label={loading ? 'Saving...' : 'Save Account'}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
});
